import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { PaginatorType, ProcessTemplateListItemType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { useGlobal } from 'context/GlobalContext';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getProcessTemplates } from 'api/apiTask';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { getStoredListPageSize } from 'utils/helpers/list/listPageSize';

const PROCESS_TEMPLATE_PICKER_PAGE_SIZE_KEY = 'process-template-picker';

/** Список довідника процесів у вікні створення процесу: пошук, категорія і серверна пагінація. */
export const useProcessDirList = (active: boolean) => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [onPage, setOnPage] = useState(() =>
        getStoredListPageSize(auth?.user?.ID, PROCESS_TEMPLATE_PICKER_PAGE_SIZE_KEY),
    );
    const [templates, setTemplates] = useState<ProcessTemplateListItemType[] | null>(null);
    const [paginator, setPaginator] = useState<PaginatorType | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (active) return;
        setSearchInput('');
        setSearch('');
        setCategoryId(null);
        setPage(1);
        setTemplates(null);
        setPaginator(null);
    }, [active]);

    useEffect(() => {
        if (!active) return;

        let cancelled = false;
        const load = async () => {
            setShowSpinner(true);
            const res = await getProcessTemplates({
                search,
                page,
                onPage,
                categoryId: categoryId || undefined,
            });
            if (cancelled) return;
            if (!res) {
                setTemplates((prev) => prev ?? []);
                showToast(t('common:messError'), 'danger');
            } else {
                setTemplates(res.templates);
                setPaginator(res.paginator || null);
            }
            setShowSpinner(false);
        };

        void load();
        return () => {
            cancelled = true;
        };
        // showToast / t стабільні для повідомлення про помилку і не мають перезапускати список
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, search, categoryId, page, onPage, reloadKey]);

    const setFilterCategoryId = (id: number | null) => {
        setCategoryId(id);
        setPage(1);
    };

    return {
        searchInput,
        setSearchInput,
        categoryId,
        setFilterCategoryId,
        page,
        setPage,
        onPage,
        setOnPage,
        paginator,
        pageSizeKey: PROCESS_TEMPLATE_PICKER_PAGE_SIZE_KEY,
        templates,
        showSpinner,
        reload: () => setReloadKey((value) => value + 1),
    };
};

export const useProcessDirRegistry = () => {
    const { t } = useTranslation(['common']);
    const { showToast } = useToast();
    const { g } = useGlobal();
    const location = useLocation();
    const onPage = useListOnPage();

    const [templates, setTemplates] = useState<ProcessTemplateListItemType[] | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [page, setPage] = useState<number | null>(null);
    const [filterCategoryId, setFilterCategoryId] = useState<number | null>(null);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);

    const prevListData = useRef<string | null>(null);
    const skipFetchOnStateChangeRef = useRef(false);

    const fetchTemplates = async (force = false) => {
        if (force) prevListData.current = null;
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        if (!page || showSpinner) return null;

        const listData = {
            search,
            page: page < 0 ? 1 : page || 1,
            onPage,
            categoryId: filterCategoryId || undefined,
        };

        if (prevListData.current === JSON.stringify(listData)) return;
        prevListData.current = JSON.stringify(listData);

        setShowSpinner(true);
        const res = await getProcessTemplates(listData);
        if (!res) {
            setShowSpinner(false);
            return showToast(t('common:messError'), 'danger');
        }
        setShowSpinner(false);

        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));
        setTemplates(res.templates);
    };

    const resetList = useCallback(async () => {
        skipFetchOnStateChangeRef.current = true;
        prevListData.current = null;
        setFilterCategoryId(null);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);

        const search = `%${g?.search?.[location.pathname] || ''}%`;
        const listData = {
            search,
            page: 1,
            onPage,
        };

        setShowSpinner(true);
        const res = await getProcessTemplates(listData);
        setShowSpinner(false);

        if (!res) {
            skipFetchOnStateChangeRef.current = false;
            return showToast(t('common:messError'), 'danger');
        }

        prevListData.current = JSON.stringify(listData);
        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));
        setTemplates(res.templates);
        skipFetchOnStateChangeRef.current = false;
    }, [g, location.pathname, onPage, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevListData.current = null;
        void fetchTemplates(true);
    });

    useEffect(() => {
        if (page === 1) void fetchTemplates(true);
        else setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [g.search?.[location.pathname], g.target]);

    useEffect(() => {
        if (skipFetchOnStateChangeRef.current) return;
        void fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filterCategoryId, onPage]);

    const paginatorState = g?.paginator?.[location.pathname];
    const paginatorPage = paginatorState?.currentPage;
    const paginatorRows = paginatorState?.countRows;
    const paginatorOnPage = paginatorState?.onPage;

    useEffect(() => {
        const nextPage = paginatorPage || 1;
        if (page !== nextPage) setPage(nextPage);
    }, [paginatorPage, paginatorRows, paginatorOnPage, page]);

    const reload = () => {
        prevListData.current = null;
        void fetchTemplates(true);
    };

    return {
        templates,
        showSpinner,
        filterCategoryId,
        setFilterCategoryId,
        reload,
        triggerListReset: () => setListResetNonce((value) => value + 1),
        triggerListRefresh: () => setListRefreshNonce((value) => value + 1),
    };
};
